import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Box, Typography, Grid, Divider, Chip, useTheme, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CopyableText from './CopyableText';

export default function InvoiceWebLayout({
  logo = '/upload/welcome.png',
  status = 'Processing',
  statusColor = 'warning',
  transactionId = '',
  details = [],
  notes = 'We appreciate your continuous patronage. Your satisfaction is our top priority!',
  supportContact = '',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copied, setCopied] = useState(false);
  const statusColors = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(transactionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Check if the detail is the client reference ID
  const isClientReference = (label) => {
    return label.toLowerCase().includes('reference') || label.toLowerCase().includes('transid') || label.toLowerCase().includes('transaction id');
  };

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 4 },
        borderRadius: 4,
        boxShadow: 8,
        background: theme.palette.background.paper,
        maxWidth: 800,
        mx: 'auto',
        mt: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: theme.palette.mode === 'light' ? '#f5f7fa' : theme.palette.grey[800],
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box component="img" src={logo} alt="logo" sx={{ width: 56, height: 56, borderRadius: 2, mr: 2 }} />
          <Typography variant="h5" fontWeight={900} color="primary.main">
            Invoice
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Chip
            label={status}
            sx={{
              bgcolor: statusColors[statusColor] || theme.palette.grey[400],
              color: theme.palette.getContrastText(statusColors[statusColor] || theme.palette.grey[400]),
              fontWeight: 700,
              fontSize: 14,
              mb: 1,
              textTransform: 'uppercase',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <CopyableText 
              value={transactionId} 
              maxWidth={isMobile ? '120px' : 'none'}
              typographyProps={{
                variant: 'subtitle2',
                fontWeight: 700,
                color: 'text.primary',
                sx: {
                  maxWidth: { xs: '120px', sm: 'none' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }
              }}
            />
          </Box>
        </Box>
      </Box>
      {/* Details */}
      <Box
        sx={{
          background: theme.palette.mode === 'light' ? '#f9fafb' : theme.palette.grey[900],
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2}>
          {details.map((item, idx) => (
            <React.Fragment key={item.label + idx}>
              <Grid item xs={6} sm={4} md={4}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={8} md={8}>
                {isClientReference(item.label) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                <Typography
                  variant={item.highlight ? 'h6' : 'body1'}
                  color={item.highlight ? 'primary.main' : 'text.primary'}
                  sx={{
                    fontWeight: item.highlight ? 900 : 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isMobile ? '150px' : 'none',
                    display: 'inline-block',
                  }}
                >
                  {String(item.value)}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(String(item.value));
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    sx={{
                      p: 0.5,
                      color: copied ? 'success.main' : 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
                ) : (
                  <Typography
                    variant={item.highlight ? 'h6' : 'body1'}
                    color={item.highlight ? 'primary.main' : 'text.primary'}
                    sx={{ fontWeight: item.highlight ? 900 : 600 }}
                  >
                    {item.value}
                  </Typography>
                )}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
      <Divider sx={{ my: 3 }} />
      {/* Footer */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} gutterBottom>
            NOTES
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {notes}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} gutterBottom>
            For support:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {supportContact}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
}

InvoiceWebLayout.propTypes = {
  logo: PropTypes.string,
  status: PropTypes.string,
  statusColor: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  transactionId: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      highlight: PropTypes.bool,
    })
  ),
  notes: PropTypes.string,
  supportContact: PropTypes.string,
};
